/**
 * Schema.org JSON-LD helpers. Pure functions returning plain objects
 * meant to be JSON.stringify'd into <script type="application/ld+json">.
 */

export const SITE_ORIGIN = "https://hari.co.id";
export const SITE_NAME_ID = "Hari.co.id";
export const SITE_NAME_EN = "Hari.co.id";

/** Public social/external profiles for sameAs. Update as accounts are created. */
export const SITE_SAME_AS: string[] = [
  "https://twitter.com/hari_coid",
  "https://www.facebook.com/hari.co.id",
  "https://www.instagram.com/hari.co.id",
];

export const SITE_CONTACT_EMAIL = "support@hari.co.id";

export type Locale = "id" | "en";

export function abs(path: string): string {
  if (path.startsWith("http")) return path;
  return `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
}

export function organizationSchema(locale: Locale = "id") {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_ORIGIN}/#organization`,
    name: locale === "en" ? SITE_NAME_EN : SITE_NAME_ID,
    alternateName: ["Hari", "hari.co.id"],
    url: SITE_ORIGIN + (locale === "en" ? "/en/" : "/"),
    logo: {
      "@type": "ImageObject",
      url: abs("/logo-pdf.png"),
      contentUrl: abs("/logo-pdf.png"),
      caption: "hari.co.id",
      width: 512,
      height: 512,
    },
    image: abs("/logo.svg"),
    sameAs: SITE_SAME_AS,
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: SITE_CONTACT_EMAIL,
        availableLanguage: ["id", "en"],
        url: abs(locale === "en" ? "/en/contact/" : "/kontak/"),
      },
    ],
    inLanguage: locale === "en" ? "en" : "id-ID",
    description:
      locale === "en"
        ? "Indonesian calendar with Gregorian, Hijri, Javanese dates plus holidays, weton and date calculators."
        : "Kalender Indonesia lengkap: Masehi, Hijriah, Jawa, libur nasional, hari penting, weton, dan kalkulator hari.",
  };
}

export function webSiteSchema(locale: Locale = "id") {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_ORIGIN}/#website`,
    name: locale === "en" ? SITE_NAME_EN : SITE_NAME_ID,
    url: SITE_ORIGIN + (locale === "en" ? "/en/" : "/"),
    inLanguage: locale === "en" ? "en" : "id-ID",
    publisher: { "@id": `${SITE_ORIGIN}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_ORIGIN}/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/** Person schema (founder/author). */
export function personSchema(opts: {
  name: string;
  jobTitle?: string;
  url?: string;
  image?: string;
  sameAs?: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: opts.name,
    ...(opts.jobTitle ? { jobTitle: opts.jobTitle } : {}),
    ...(opts.url ? { url: abs(opts.url) } : {}),
    ...(opts.image ? { image: opts.image } : {}),
    worksFor: { "@id": `${SITE_ORIGIN}/#organization` },
    ...(opts.sameAs && opts.sameAs.length ? { sameAs: opts.sameAs } : {}),
  };
}

export interface BreadcrumbItem { name: string; url: string }
export function breadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: abs(it.url),
    })),
  };
}

export type WebPageType =
  | "WebPage"
  | "AboutPage"
  | "ContactPage"
  | "CollectionPage"
  | "ItemPage";

export function webPageSchema(opts: {
  url: string;
  name: string;
  description: string;
  type?: WebPageType;
  locale?: Locale;
  datePublished?: string;
  dateModified?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": opts.type ?? "WebPage",
    name: opts.name,
    url: abs(opts.url),
    description: opts.description,
    inLanguage: opts.locale === "en" ? "en" : "id-ID",
    isPartOf: { "@type": "WebSite", name: opts.locale === "en" ? SITE_NAME_EN : SITE_NAME_ID, url: SITE_ORIGIN },
    ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
    ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
  };
}

/** Minimal Privacy Policy / Terms page (uses CreativeWork @type with extra hints). */
export function policyPageSchema(opts: {
  url: string;
  name: string;
  description: string;
  kind: "PrivacyPolicy" | "TermsOfService";
  locale?: Locale;
  dateModified?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: opts.name,
    url: abs(opts.url),
    description: opts.description,
    inLanguage: opts.locale === "en" ? "en" : "id-ID",
    about: { "@type": "Thing", name: opts.kind },
    isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
    publisher: { "@id": `${SITE_ORIGIN}/#organization` },
    ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
  };
}

export interface FAQPair { q: string; a: string }
export function faqSchema(pairs: FAQPair[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: pairs.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

export const INDONESIA_PLACE = {
  "@type": "Place" as const,
  name: "Indonesia",
  address: {
    "@type": "PostalAddress" as const,
    addressCountry: "ID",
    addressRegion: "Indonesia",
  },
};

export const SITE_ORGANIZER = {
  "@type": "Organization" as const,
  name: SITE_NAME_ID,
  url: SITE_ORIGIN + "/",
  logo: { "@type": "ImageObject" as const, url: abs("/apple-touch-icon.png") },
};

export const DEFAULT_EVENT_IMAGE = abs("/og-default.svg");

export interface EventInput {
  name: string;
  date: string;       // ISO YYYY-MM-DD
  endDate?: string;
  description?: string;
  url?: string;
}
export function eventSchema(ev: EventInput, locale: Locale = "id") {
  const desc =
    ev.description ??
    (locale === "en"
      ? `${ev.name} commemorated in Indonesia on ${ev.date}.`
      : `${ev.name} diperingati di Indonesia pada ${ev.date}.`);
  const offerUrl = ev.url ? abs(ev.url) : SITE_ORIGIN + "/";
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: ev.name,
    startDate: ev.date,
    endDate: ev.endDate ?? ev.date,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    location: INDONESIA_PLACE,
    organizer: SITE_ORGANIZER,
    performer: SITE_ORGANIZER,
    image: [DEFAULT_EVENT_IMAGE],
    description: desc,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "IDR",
      availability: "https://schema.org/InStock",
      url: offerUrl,
      validFrom: ev.date,
    },
    ...(ev.url ? { url: abs(ev.url) } : {}),
    inLanguage: locale === "en" ? "en" : "id-ID",
  };
}

export function itemListSchema(events: EventInput[], locale: Locale = "id") {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: events.map((ev, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: ev.name,
      url: ev.url ? abs(ev.url) : undefined,
      item: eventSchema(ev, locale),
    })),
  };
}

/** Helper: convert a list of LD objects into TanStack head().scripts entries. */
export function ldScripts(objects: unknown[]): { type: "application/ld+json"; children: string }[] {
  return objects.filter(Boolean).map((obj) => ({
    type: "application/ld+json",
    children: JSON.stringify(obj),
  }));
}

/** Dataset schema with required license + creator (for Hijri month/year pages). */
export const SITE_DATASET_LICENSE = "https://creativecommons.org/licenses/by/4.0/";
export function datasetSchema(opts: {
  name: string;
  description: string;
  url: string;
  temporalCoverage?: string;
  about?: unknown;
  locale?: Locale;
  keywords?: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: opts.name,
    description: opts.description,
    url: abs(opts.url),
    inLanguage: opts.locale === "en" ? "en" : "id-ID",
    license: SITE_DATASET_LICENSE,
    creator: SITE_ORGANIZER,
    publisher: SITE_ORGANIZER,
    isAccessibleForFree: true,
    ...(opts.temporalCoverage ? { temporalCoverage: opts.temporalCoverage } : {}),
    ...(opts.about ? { about: opts.about } : {}),
    ...(opts.keywords && opts.keywords.length ? { keywords: opts.keywords } : {}),
  };
}

/** WebApplication schema for tools (Cek Weton, Kalkulator Hari). */
export function webApplicationSchema(opts: {
  name: string;
  url: string;
  description: string;
  applicationCategory?: string;
  locale?: Locale;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: opts.name,
    url: abs(opts.url),
    description: opts.description,
    applicationCategory: opts.applicationCategory ?? "UtilitiesApplication",
    operatingSystem: "Any",
    offers: { "@type": "Offer", price: "0", priceCurrency: "IDR" },
    inLanguage: opts.locale === "en" ? "en" : "id-ID",
  };
}

/** Article schema for editorial pages (e.g. /hari/<slug>). */
export function articleSchema(opts: {
  url: string;
  headline: string;
  description: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
  locale?: Locale;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: opts.headline,
    description: opts.description,
    mainEntityOfPage: { "@type": "WebPage", "@id": abs(opts.url) },
    inLanguage: opts.locale === "en" ? "en" : "id-ID",
    author: { "@type": "Organization", name: opts.authorName ?? SITE_NAME_ID },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME_ID,
      logo: { "@type": "ImageObject", url: abs("/apple-touch-icon.png") },
    },
    ...(opts.image ? { image: opts.image } : {}),
    ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
    ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
  };
}
